import type { MockedResponse } from "@apollo/client/testing";
import { COMMISSION_LINES_QUERY } from "../lines/queries";
import type { CommissionLineNode } from "../lines/types";
import { commissionFundingBatchFixtures } from "./fundingFixtures";
import { buildConnection } from "./connection";
import { createCatalogMock, withTypenames, type MockResolver } from "./mockUtils";
import { runtimeCommissionLines, runtimeFundingBatches, runtimeFundingBatchLines } from "./runtimeState";

export function withLineTypenames(line: CommissionLineNode) {
  return {
    __typename: "BackofficeCommissionLine",
    ...line,
    person: withTypenames(line.person, "BackofficeCommissionEnrollmentPerson"),
    campaign: {
      __typename: "BackofficeCommissionCampaign",
      ...line.campaign,
      partner: withTypenames(line.campaign.partner, "BackofficeCommissionPartner"),
      program: withTypenames(line.campaign.program, "BackofficeCommissionProgramSummary")
    }
  };
}

function getFundingBatchReservedLineIds() {
  const reservedLineIds = new Set<string>();

  for (const batch of commissionFundingBatchFixtures) {
    if (batch.status === "VOID") continue;
    for (const line of runtimeFundingBatchLines[batch.id] ?? []) {
      reservedLineIds.add(line.id);
    }
  }

  for (const [batchId, batch] of Object.entries(runtimeFundingBatches)) {
    if (batch.status === "VOID") continue;
    for (const line of runtimeFundingBatchLines[batchId] ?? []) {
      reservedLineIds.add(line.id);
    }
  }

  return reservedLineIds;
}

function buildLinesMockData(variables: Record<string, unknown>) {
  const fundingStatus = variables.fundingStatus ? String(variables.fundingStatus) : undefined;
  const payoutEligibilityStatus = variables.payoutEligibilityStatus
    ? String(variables.payoutEligibilityStatus)
    : undefined;
  const campaignId = variables.campaignId ? String(variables.campaignId) : undefined;
  const reservedLineIds = fundingStatus === "PENDING" ? getFundingBatchReservedLineIds() : new Set<string>();

  const nodes = runtimeCommissionLines
    .filter((line) => {
      if (fundingStatus && line.fundingStatus !== fundingStatus) return false;
      if (reservedLineIds.has(line.id)) return false;
      if (payoutEligibilityStatus && line.payoutEligibilityStatus !== payoutEligibilityStatus) return false;
      if (campaignId && line.campaign.id !== campaignId) return false;
      return true;
    })
    .map(withLineTypenames);

  return {
    commissionLines: buildConnection("BackofficeCommissionLineConnection", nodes)
  };
}

export const commissionLinesApolloMock = createCatalogMock(COMMISSION_LINES_QUERY, buildLinesMockData({}));

export const linesApolloMocks: MockedResponse[] = [commissionLinesApolloMock];

export const linesMockResults: Record<string, Record<string, unknown>> = {
  CommissionLines: buildLinesMockData({})
};

export const linesVariableMockResolvers: Record<string, MockResolver> = {
  CommissionLines: (variables) => buildLinesMockData(variables)
};
